/**
 * "Inserir": os campos que o evento do começo traz, como atalho para o texto
 * de uma expressão. Clicar num põe o caminho do campo onde está o cursor.
 *
 * Sem prefixo: o serviço avalia a expressão sobre o próprio conteúdo do evento
 * (`message.author_role`), e um `evento.` na frente nunca casaria.
 *
 * Os campos vêm do catálogo (`eventos[].contexto`); a automação de agenda não
 * tem evento, e não mostra nada.
 */

import { useRef, type ReactNode, type Ref } from 'react';

type Alvo = HTMLInputElement | HTMLTextAreaElement;

export function ComCamposDoEvento({
  campos,
  valor,
  aoMudar,
  children,
}: {
  campos: readonly string[];
  valor: string;
  aoMudar: (valor: string) => void;
  /** O campo de texto, com a `ref` que o atalho usa para achar o cursor. */
  children: (ref: Ref<HTMLInputElement & HTMLTextAreaElement>) => ReactNode;
}): ReactNode {
  const alvo = useRef<Alvo>(null);

  const inserir = (campo: string): void => {
    const el = alvo.current;
    const pedaco = campo;
    const inicio = el?.selectionStart ?? valor.length;
    const fim = el?.selectionEnd ?? valor.length;
    aoMudar(valor.slice(0, inicio) + pedaco + valor.slice(fim));
    // O cursor volta para o campo, logo depois do que entrou.
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(inicio + pedaco.length, inicio + pedaco.length);
    });
  };

  return (
    <div className="editor-do-mapa__com-campos">
      {children(alvo as Ref<HTMLInputElement & HTMLTextAreaElement>)}
      {campos.length > 0 && (
        <div
          className="editor-do-mapa__inserir"
          role="group"
          aria-label="Inserir um campo do evento"
        >
          <span className="editor-do-mapa__inserir-rotulo" aria-hidden="true">
            Inserir:
          </span>
          {campos.map((c) => (
            <button
              key={c}
              type="button"
              className="editor-do-mapa__inserir-campo"
              aria-label={`Inserir ${c}`}
              onClick={() => inserir(c)}
            >
              {c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
